// @unocss-include

import { EditorState, StateEffect, StateField } from '@codemirror/state';
import { type Tooltip, type TooltipView, showTooltip } from '@codemirror/view';
import { filePathStateField } from './state';
import { chatStore } from '~/lib/stores/chat';

export const aiTooltipStateEffect = StateEffect.define<boolean>();

export const aiTooltipStateField = StateField.define<readonly Tooltip[]>({
  create: () => [],
  update(_tooltips, transaction) {
    if (transaction.state.readOnly) {
      return [];
    }

    for (const effect of transaction.effects) {
      if (effect.is(aiTooltipStateEffect) && effect.value) {
        return getTooltip(transaction.state);
      }
    }

    return [];
  },
  provide: (field) => {
    return showTooltip.computeN([field], (state) => state.field(field));
  },
});

function getTooltip(state: EditorState) {
  if (state.readOnly) {
    return [];
  }

  return state.selection.ranges
    .filter((range) => {
      return !range.empty;
    })
    .map((range) => {
      return new AiTooltip(state, range.head);
    });
}

class AiTooltip implements Tooltip {
  above = false;
  strictSide = false;
  arrow = false;

  constructor(
    private _state: EditorState,
    public pos: number,
  ) {
    if (this._state.selection.main.from === this._state.selection.main.head) {
      this.above = true;
    }
  }

  create() {
    const divElement = document.createElement('div');
    divElement.className = 'cm-ai-tooltip';

    divElement.setAttribute('style', `transform: translateY(${this.above ? '-' : ''}4px)`);

    const askBoltButton = document.createElement('button');
    askBoltButton.className = 'px-2 py-1 flex items-center gap-1 text-md';

    const iconSpan = document.createElement('span');
    iconSpan.className = 'i-bolt:stars';

    const buttonText = document.createTextNode('Ask Bolt');

    askBoltButton.appendChild(iconSpan);
    askBoltButton.appendChild(buttonText);

    const mouseDownListener = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const selection = this._state.selection.main;
      const line = this._state.doc.lineAt(selection.from);
      const nextLine = this._state.doc.lineAt(selection.to);

      const text = reindentText(this._state.sliceDoc(line.from, nextLine.to));

      const currentFilePath = this._state.field(filePathStateField);

      chatStore.addToSelection({
        filePath: currentFilePath ?? '',
        text,
        range: { from: line.from, to: nextLine.to },
      });
    };

    askBoltButton.addEventListener('mousedown', mouseDownListener);

    divElement.appendChild(askBoltButton);

    return {
      dom: divElement,
      destroy: () => {
        askBoltButton.removeEventListener('mousedown', mouseDownListener);
      },
    } satisfies TooltipView;
  }
}

function reindentText(text: string): string {
  const lines = text.split('\n');

  const minIndent = lines
    .filter((line) => line.trim().length > 0)
    .reduce((min, line) => Math.min(min, line.length - line.trimStart().length), Infinity);

  return lines
    .map((line) => {
      const currentIndent = line.length - line.trimStart().length;
      return ' '.repeat(Math.max(0, currentIndent - minIndent)) + line.trim();
    })
    .join('\n');
}
